import React, { useEffect, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

const CAMERAS_API = "https://f0usr04663.execute-api.us-east-1.amazonaws.com/cameras";
type Camera = { cameraId?: string; planType?: string; planName?: string };
type Props = { camera: Camera };
type UsageResponse = {
  planType?: string;
  planName?: string;
  cycle: { start: string; end: string; daysRemaining: number; status: string };
  rates: {
    currency?: string;
    aiInputPerMillionTokens?: number;
    aiOutputPerMillionTokens?: number;
  };
  quantities: {
    aiMonitoringEvents?: number;
    aiInputTokens?: number;
    aiOutputTokens?: number;
  };
  charges: {
    basePlan?: number;
    aiInput?: number;
    aiOutput?: number;
    aiMonitoring?: number;
    total: number;
  };
};

const money = (value: number) => `${Number(value || 0).toFixed(2)}`;
const tokenRate = (value: number) => `${Number(value || 0).toFixed(4)}`;
const count = (value: number) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number(value || 0));
const date = (value: string) => {
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : value;
};
const muted: React.CSSProperties = { color: "#64748b", fontSize: 12, lineHeight: 1.5 };

export default function CameraUsageCyclePanelFixed({ camera }: Props) {
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;
    async function load() {
      if (!camera.cameraId || !camera.planType) { setUsage(null); return; }
      setLoading(true);
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (!token) throw new Error("Your session has expired.");
        const response = await fetch(`${CAMERAS_API}/${encodeURIComponent(camera.cameraId)}/billing-usage`, { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } });
        const text = await response.text();
        const json = text ? JSON.parse(text) : null;
        if (!response.ok) throw new Error(json?.message || `Unable to load usage (${response.status})`);
        if (!json?.cycle || !json?.charges || !Number.isFinite(Number(json.charges.total))) throw new Error("Billing usage is not available yet.");
        if (!cancelled) { setUsage(json); setError(""); }
      } catch (err) {
        if (!cancelled) { setUsage(null); setError(err instanceof Error ? err.message : "Unable to load billing usage"); }
      } finally { if (!cancelled) setLoading(false); }
    }
    void load();
    timer = setInterval(() => void load(), 60000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [camera.cameraId, camera.planType]);

  if (!camera.planType) return <div style={panel}><strong>Current 30-day cycle</strong><span style={muted}>Assign a billing plan to start usage tracking.</span></div>;
  if (!usage) return <div style={panel}><strong>Current 30-day cycle</strong><span style={{ ...muted, color: error ? "#b91c1c" : muted.color }}>{error || (loading ? "Loading usage…" : "Usage is not available yet.")}</span></div>;

  const inputTokens = Number(usage.quantities?.aiInputTokens || 0);
  const outputTokens = Number(usage.quantities?.aiOutputTokens || 0);
  const inputRate = Number(
    usage.rates?.aiInputPerMillionTokens ?? 0.175834,
  );
  const outputRate = Number(
    usage.rates?.aiOutputPerMillionTokens ?? 0.703334,
  );
  const planName = usage.planName || camera.planName || "Camera subscription";

  return <section style={panel} aria-label="Current billing cycle usage">
    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
      <div><div style={{ color: "#2563eb", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: ".06em" }}>Current 30-day cycle</div><strong style={{ fontSize: 17 }}>{date(usage.cycle.start)} → {date(usage.cycle.end)}</strong><div style={muted}>{usage.cycle.daysRemaining} day{usage.cycle.daysRemaining === 1 ? "" : "s"} remaining · {usage.cycle.status}</div></div>
      <div style={{ textAlign: "right" }}><div style={muted}>Estimated total</div><div style={{ color: "#0f172a", fontSize: 27, fontWeight: 900 }}>{money(usage.charges.total)}</div></div>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10 }}>
      <div style={row}><div><strong>{planName}</strong><div style={muted}>Camera subscription</div></div><strong>{money(Number(usage.charges.basePlan || 0))}</strong></div>
      <div style={{ ...row, borderColor: "#bfdbfe", background: "#f8fbff" }}><div><strong>AI Monitoring</strong><div style={muted}>{count(inputTokens)} input · {count(outputTokens)} output tokens</div><div style={muted}>{tokenRate(inputRate)} input · {tokenRate(outputRate)} output / 1M</div></div><strong>{money(Number(usage.charges.aiMonitoring || 0))}</strong></div>
    </div>

    <div style={{ ...muted, paddingTop: 2 }}>Person and vehicle analytics, cloud confirmation, retained video, live viewing, and archive playback are included in the selected camera plan. AI Monitoring is the only token-metered add-on shown here; event counts are informational only.</div>
  </section>;
}

const panel: React.CSSProperties = { display: "grid", gap: 14, padding: 18, border: "1px solid #cbd5e1", borderRadius: 14, background: "#fff" };
const row: React.CSSProperties = { display: "flex", justifyContent: "space-between", gap: 12, padding: "13px 14px", border: "1px solid #e2e8f0", borderRadius: 10, background: "#f8fafc", color: "#334155", fontSize: 12 };
